import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

import {
  getBorrowerFriendlyStatus,
  latestApplication,
  MlaAddress,
  MlaBorrowerFacts,
  MlaDashboardResponse,
  MlaLoanApplicationModel,
  MlaLoanDashboardCategory,
  MlaLoanFacts,
  selectedApplicationId,
} from '@ld.consumer-portal/models';
import { AuthService, FirebaseService, LoggingService } from '@ld.consumer-portal/utils';
import { MlaAuthService } from './mla-auth.service';
import { MlaBorrowerService } from './mla-borrower.service';

@Injectable({
  providedIn: 'root',
})
export class MlaDashboardService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(MlaAuthService);
  private readonly borrowerService = inject(MlaBorrowerService);
  private readonly loggingService = inject(LoggingService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly userAuthService = inject(AuthService);

  private dashboardCache = new Map<string, { response: MlaDashboardResponse; timestamp: number }>();

  private inFlightRequests = new Map<string, Promise<MlaDashboardResponse>>();

  private readonly DASHBOARD_CACHE_TTL_MS = 2 * 60 * 1000;

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  private cacheEpoch = 0;

  private readonly _cacheVersion = signal(0);

  /**
   * Reactive counter bumped whenever the dashboard cache changes (cleared or a
   * response is stored). Consumers that resolve borrower data via the plain
   * (non-signal) cache lookups can read this signal to re-resolve when the cache
   * reloads (e.g., after a borrower switch re-fetches with the new token).
   */
  readonly cacheVersion = this._cacheVersion.asReadonly();

  /**
   * Borrower name resolved for the selected (or latest) origination application.
   * Re-resolves whenever the dashboard cache reloads (e.g., after a borrower switch
   * re-fetches with the new token, marking the selected borrower as logged-in).
   * Null when no application is selected/cached or the loan is not found.
   */
  readonly liveOriginationBorrowerName = computed<string | null>(() => {
    this.cacheVersion();

    const targetAppId = selectedApplicationId() ?? latestApplication()?.id ?? null;
    return targetAppId ? this.getDisplayBorrowerNameForLoan(targetAppId) : null;
  });

  clearDashboardCache(): void {
    this.cacheEpoch++;
    this.dashboardCache.clear();
    this.inFlightRequests.clear();
    this._cacheVersion.update((v) => v + 1);
  }

  private get dashboardMode(): 'v1' | 'v2' | 'both' {
    const mode = this.firebaseService.getAppConfigValue('mla_dashboard_mode');
    if (mode === 'v2' || mode === 'both') return mode;
    return 'v1';
  }

  private get v1Endpoint(): string {
    return `${this.mlaApiUrl}api/v1/dashboard`;
  }

  private get v2Endpoint(): string {
    return `${this.mlaApiUrl}api/v1/dashboard/session`;
  }

  async getDashboard(borrowerToken: string): Promise<MlaDashboardResponse> {
    const cached = this.dashboardCache.get(borrowerToken);
    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < this.DASHBOARD_CACHE_TTL_MS) {
        return cached.response;
      }
    }

    const inFlight = this.inFlightRequests.get(borrowerToken);
    if (inFlight) {
      return inFlight;
    }

    const epoch = this.cacheEpoch;
    const promise = this.fetchDashboardByMode(borrowerToken)
      .then((response) => {
        if (response.data?.loans) {
          response = { ...response, data: { ...response.data, loans: this.deduplicateLoans(response.data.loans) } };
        }
        if (this.cacheEpoch === epoch && response.success) {
          this.dashboardCache.set(borrowerToken, { response, timestamp: Date.now() });
          this._cacheVersion.update((v) => v + 1);
        }
        if (this.inFlightRequests.get(borrowerToken) === promise) {
          this.inFlightRequests.delete(borrowerToken);
        }
        return response;
      })
      .catch((error) => {
        if (this.inFlightRequests.get(borrowerToken) === promise) {
          this.inFlightRequests.delete(borrowerToken);
        }
        this.loggingService.logException(error, 'MlaDashboardService: Error fetching dashboard');
        return { success: false, data: { loans: [] } } as MlaDashboardResponse;
      });

    this.inFlightRequests.set(borrowerToken, promise);
    return promise;
  }

  private async fetchDashboardByMode(borrowerToken: string): Promise<MlaDashboardResponse> {
    const mode = this.dashboardMode;
    const params = new HttpParams().set('borrowerToken', borrowerToken);

    if (mode === 'both') {
      const requestId = crypto.randomUUID();
      const paramsWithRequestId = params.set('requestId', requestId);
      const emptyResponse: MlaDashboardResponse = { success: false, data: { loans: [] } };

      const v1Start = Date.now();
      const v1Promise = firstValueFrom(
        this.http.get<MlaDashboardResponse>(this.v1Endpoint, { params: paramsWithRequestId }),
      )
        .then((res) => ({ res, runtime: Date.now() - v1Start }))
        .catch((error) => {
          this.loggingService.logException(error, 'MlaDashboardService: Error fetching v1 dashboard');
          return { res: emptyResponse, runtime: Date.now() - v1Start };
        });

      const v2Start = Date.now();
      const v2Promise = firstValueFrom(
        this.http.get<MlaDashboardResponse>(this.v2Endpoint, { params: paramsWithRequestId }),
      )
        .then((res) => ({ res, runtime: Date.now() - v2Start }))
        .catch((error) => {
          this.loggingService.logException(error, 'MlaDashboardService: Error fetching v2 dashboard');
          return { res: emptyResponse, runtime: Date.now() - v2Start };
        });

      const [v1, v2] = await Promise.all([v1Promise, v2Promise]);
      const v1Result = v1.res;
      const v2Result = v2.res;
      const v1Runtime = v1.runtime;
      const v2Runtime = v2.runtime;

      try {
        this.logDashboardComparison(requestId, borrowerToken, v1Result, v2Result, v1Runtime, v2Runtime);
      } catch (error) {
        this.loggingService.logException(error, 'MlaDashboardService: Error in dashboard comparison logging');
      }

      return v1Result;
    }

    if (mode === 'v2') {
      return firstValueFrom(this.http.get<MlaDashboardResponse>(this.v2Endpoint, { params }));
    }

    return firstValueFrom(this.http.get<MlaDashboardResponse>(this.v1Endpoint, { params }));
  }

  private async fetchV1Dashboard(
    borrowerToken: string,
    requestId?: string,
  ): Promise<{ response: MlaDashboardResponse; runtime: number }> {
    const emptyResponse: MlaDashboardResponse = { success: false, data: { loans: [] } };
    let params = new HttpParams().set('borrowerToken', borrowerToken);
    if (requestId) {
      params = params.set('requestId', requestId);
    }
    const start = Date.now();
    try {
      const response = await firstValueFrom(this.http.get<MlaDashboardResponse>(this.v1Endpoint, { params }));
      return { response, runtime: Date.now() - start };
    } catch (error) {
      this.loggingService.logException(error, 'MlaDashboardService: Error fetching v1 dashboard');
      return { response: emptyResponse, runtime: Date.now() - start };
    }
  }

  private get serviceLogUrl(): string {
    const cpApiUrl = this.env.appSettings.cpApiUrl || '';
    const apiNamespace = this.env.appSettings.apiNamespace || 'api/v1';
    return `${cpApiUrl}/${apiNamespace}/service/log`;
  }

  private normalizeForDiff(o: any): any { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (o === null || o === undefined) {
      return o;
    }
    if (o instanceof Date) {
      return o.toISOString();
    }
    if (typeof o === 'object') {
      if (Array.isArray(o)) {
        return [...o.map((x) => this.normalizeForDiff(x))].sort((a, b) => {
          const sa = JSON.stringify(a);
          const sb = JSON.stringify(b);
          return sa > sb ? 1 : sa < sb ? -1 : 0;
        });
      } else { // object
        // recreate the object with sorted keys
        return Object.fromEntries(
          Object.entries(o).sort((a, b) => a[0] > b[0] ? 1 : a[0] < b[0] ? -1 : 0).map(e => [e[0], this.normalizeForDiff(e[1])])
        );
      }
    }
    return o;
  }

  private logDashboardComparison(
    requestId: string,
    borrowerToken: string,
    v1Result: MlaDashboardResponse,
    v2Result: MlaDashboardResponse,
    v1Runtime: number,
    v2Runtime: number,
  ): void {
    const v1Loans = v1Result.data?.loans ?? [];
    const v2Loans = v2Result.data?.loans ?? [];
    const v1LoanNumbers = v1Loans.map((l) => l.displayLoanId);
    const v2LoanNumbers = v2Loans.map((l) => l.displayLoanId);
    const v1SortedIds = [...v1LoanNumbers].sort();
    const v2SortedIds = [...v2LoanNumbers].sort();
    const v1Set = new Set(v1LoanNumbers);
    const v2Set = new Set(v2LoanNumbers);
    const differingLoanNumbers = [
      ...v1LoanNumbers.filter((id) => !v2Set.has(id)),
      ...v2LoanNumbers.filter((id) => !v1Set.has(id)),
    ].sort();

    const { firstName, lastName } = this.userAuthService.extractNamesFromB2cToken(true);

    const comparisonPayload = {
      RequestID: requestId,
      LoginEmail: this.userAuthService.getUserEmail() ?? '',
      FirstName: firstName,
      LastName: lastName,
      BorrowerID: borrowerToken,
      DateTime: new Date().toISOString(),
      V1_not_equal_V2: JSON.stringify(v1SortedIds) !== JSON.stringify(v2SortedIds),
      V1_data_not_equal_V2_data: JSON.stringify(this.normalizeForDiff(v1Loans)) !== JSON.stringify(this.normalizeForDiff(v2Loans)),
      V1_not_equal_V2_loan_numbers: differingLoanNumbers,
      V1_count_greater_than_V2_count: v1Loans.length > v2Loans.length,
      V1_count: v1Loans.length,
      V2_count: v2Loans.length,
      V1_runtime: `${v1Runtime}ms`,
      V2_runtime: `${v2Runtime}ms`,
      V1_loan_numbers: v1LoanNumbers,
      V2_loan_numbers: v2LoanNumbers,
    };

    const logRequest = {
      items: [
        {
          type: 'MlaDashboardComparison',
          message: JSON.stringify(comparisonPayload),
          level: 'Info',
          timestamp: new Date().toISOString(),
          path: '/dashboard',
          origin: 'MlaDashboardService',
        },
      ],
    };

    this.http.post(this.serviceLogUrl, logRequest).subscribe({
      error: (error) =>
        this.loggingService.logException(error, 'MlaDashboardService: Error logging dashboard comparison'),
    });


  }

  async getDashboardWithStoredToken(): Promise<MlaDashboardResponse> {
    const token = this.authService.dashboardToken;
    if (!token) {
      return { success: false, data: { loans: [] } };
    }
    return this.getDashboard(token);
  }

  async getActiveLoanApplications(): Promise<MlaLoanFacts[]> {
    try {
      const response = await this.getDashboardForSelectedBorrower();
      if (!response?.data?.loans) {
        return [];
      }

      return response.data.loans.filter(
        (loan: MlaLoanFacts) =>
          loan.loanDashboardCategory === 'Unsubmitted' || loan.loanDashboardCategory === 'PendingApproval',
      );
    } catch (error) {
      this.loggingService.logException(error, 'MlaDashboardService: Error getting active applications');
      return [];
    }
  }

  async getLoanApplications(): Promise<MlaLoanApplicationModel[]> {
    try {
      const loans = await this.getAllLoans();
      if (!loans.length) {
        return [];
      }
      const mapped = loans.map((loan) => this.mapToLoanApplication(loan));
      return mapped;
    } catch (error) {
      this.loggingService.logException(error, 'MlaDashboardService: Error getting loan applications');
      return [];
    }
  }

  async getAllLoans(): Promise<MlaLoanFacts[]> {
    try {
      const borrowers = await this.borrowerService.getBorrowerInfo();
      const borrowerTokens = borrowers.map((b) => b.borrowerToken).filter((token): token is string => !!token);

      if (borrowerTokens.length <= 1) {
        const response = await this.getDashboardForSelectedBorrower();
        const loans = response?.data?.loans || [];
        return loans;
      }

      return this.fetchAndMergeAllBorrowerLoans(borrowerTokens);
    } catch (error) {
      this.loggingService.logException(error, 'MlaDashboardService: Error getting all loans');
      return [];
    }
  }

  private async getDashboardForSelectedBorrower(): Promise<MlaDashboardResponse> {
    const storedToken = this.authService.dashboardToken;
    if (storedToken) {
      return this.getDashboard(storedToken);
    }

    const borrowers = await this.borrowerService.getBorrowerInfo();
    const primary = borrowers.find((b) => b.isPrimary) || borrowers.find((b) => !!b.borrowerToken) || borrowers[0];
    if (!primary?.borrowerToken) {
      return { success: false, data: { loans: [] } } as MlaDashboardResponse;
    }

    return this.getDashboard(primary.borrowerToken);
  }

  private async fetchAndMergeAllBorrowerLoans(borrowerTokens: string[]): Promise<MlaLoanFacts[]> {
    if (this.dashboardMode === 'both') {
      return this.fetchAndMergeWithComparison(borrowerTokens);
    }

    const emptyResponse: MlaDashboardResponse = { success: false, data: { loans: [] } };
    const dashboardPromises = borrowerTokens.map((token) => this.getDashboard(token).catch(() => emptyResponse));
    const dashboards = await Promise.all(dashboardPromises);

    const allLoans: MlaLoanFacts[] = [];
    for (const dashboard of dashboards) {
      if (dashboard?.success && dashboard.data?.loans) {
        allLoans.push(...dashboard.data.loans);
      }
    }

    return this.deduplicateLoans(allLoans);
  }

  private async fetchAndMergeWithComparison(borrowerTokens: string[]): Promise<MlaLoanFacts[]> {
    const requestId = crypto.randomUUID();

    const v1Results = await Promise.all(borrowerTokens.map((token) => this.fetchV1Dashboard(token, requestId)));

    const allV1Loans: MlaLoanFacts[] = [];
    let maxV1Runtime = 0;
    const epoch = this.cacheEpoch;
    for (let i = 0; i < v1Results.length; i++) {
      const { response, runtime } = v1Results[i];
      maxV1Runtime = Math.max(maxV1Runtime, runtime);
      if (response?.success && response.data?.loans) {
        allV1Loans.push(...response.data.loans);
      }
      if (this.cacheEpoch === epoch && response?.success) {
        const cachedResponse = {
          ...response,
          data: { ...response.data, loans: this.deduplicateLoans(response.data?.loans ?? []) },
        };
        this.dashboardCache.set(borrowerTokens[i], { response: cachedResponse, timestamp: Date.now() });
      }
    }
    const deduplicatedV1Loans = this.deduplicateLoans(allV1Loans);

    const emptyResponse: MlaDashboardResponse = { success: false, data: { loans: [] } };
    const v2Params = new HttpParams().set('requestId', requestId);
    const v2Start = Date.now();
    let v2Result: MlaDashboardResponse;
    try {
      v2Result = await firstValueFrom(this.http.get<MlaDashboardResponse>(this.v2Endpoint, { params: v2Params }));
    } catch (error) {
      this.loggingService.logException(error, 'MlaDashboardService: Error fetching v2 dashboard');
      v2Result = emptyResponse;
    }
    const v2Runtime = Date.now() - v2Start;

    const combinedV1Response: MlaDashboardResponse = {
      success: allV1Loans.length > 0,
      data: { loans: deduplicatedV1Loans },
    };

    try {
      this.logDashboardComparison(
        requestId,
        borrowerTokens.join(','),
        combinedV1Response,
        v2Result,
        maxV1Runtime,
        v2Runtime,
      );
    } catch (error) {
      this.loggingService.logException(error, 'MlaDashboardService: Error in dashboard comparison logging');
    }

    return deduplicatedV1Loans;
  }

  private deduplicateLoans(loans: MlaLoanFacts[]): MlaLoanFacts[] {
    const loanMap = new Map<string, MlaLoanFacts>();

    for (const loan of loans) {
      const key = loan.displayLoanId || loan.loanID;
      const existing = loanMap.get(key);
      if (!existing) {
        loanMap.set(key, { ...loan, borrowers: [...loan.borrowers] });
        continue;
      }

      const existingIsLoggedIn = existing.borrowers.some((b) => b.isLoggedInBorrower);
      const incomingIsLoggedIn = loan.borrowers.some((b) => b.isLoggedInBorrower);

      const shouldReplace =
        (!existingIsLoggedIn && incomingIsLoggedIn) ||
        (existingIsLoggedIn === incomingIsLoggedIn && !existing.status && !!loan.status) ||
        (existingIsLoggedIn === incomingIsLoggedIn &&
          !!existing.status === !!loan.status &&
          (new Date(loan.lastModifiedDate).getTime() || 0) > (new Date(existing.lastModifiedDate).getTime() || 0));

      if (shouldReplace) {
        const mergedBorrowers = this.mergeBorrowers(loan.borrowers, existing.borrowers);
        loanMap.set(key, { ...loan, borrowers: mergedBorrowers });
      } else {
        existing.borrowers = this.mergeBorrowers(existing.borrowers, loan.borrowers);
      }
    }

    return Array.from(loanMap.values());
  }

  private mergeBorrowers(primary: MlaBorrowerFacts[], secondary: MlaBorrowerFacts[]): MlaBorrowerFacts[] {
    const merged: MlaBorrowerFacts[] = [...primary];
    const seenBorrowerIds = new Set<string>();
    const seenEmails = new Set<string>();

    for (const b of merged) {
      if (b.borrowerID) {
        seenBorrowerIds.add(b.borrowerID);
      }
      if (b.email) {
        seenEmails.add(b.email.toLowerCase());
      }
    }

    for (const borrower of secondary) {
      if (borrower.borrowerID && seenBorrowerIds.has(borrower.borrowerID)) {
        continue;
      }

      if (!borrower.borrowerID && borrower.email && seenEmails.has(borrower.email.toLowerCase())) {
        continue;
      }

      if (borrower.borrowerID && borrower.email && seenEmails.has(borrower.email.toLowerCase())) {
        const emptyIdIndex = merged.findIndex(
          (b) => !b.borrowerID && b.email && b.email.toLowerCase() === borrower.email.toLowerCase(),
        );
        if (emptyIdIndex !== -1) {
          merged[emptyIdIndex] = borrower;
          seenBorrowerIds.add(borrower.borrowerID);
          continue;
        }
      }

      merged.push(borrower);
      if (borrower.borrowerID) {
        seenBorrowerIds.add(borrower.borrowerID);
      }
      if (borrower.email) {
        seenEmails.add(borrower.email.toLowerCase());
      }
    }

    return merged;
  }

  getLoggedInBorrowerIdForLoan(loanId: string): string | null {
    const normalizedId = this.stripEpsLoanPrefix(loanId);
    for (const entry of this.dashboardCache.values()) {
      const loan = entry.response?.data?.loans?.find((l) => this.stripEpsLoanPrefix(l.loanID) === normalizedId);
      if (loan) {
        const loggedInBorrower = loan.borrowers.find((b) => b.isLoggedInBorrower);
        if (loggedInBorrower) {
          return loggedInBorrower.borrowerID;
        }
        return loan.borrowers[0]?.borrowerID || null;
      }
    }
    return null;
  }

  getLoanBorrowerIds(loanId: string): string[] {
    const normalizedId = this.stripEpsLoanPrefix(loanId);
    const allBorrowerIds = new Set<string>();
    for (const entry of this.dashboardCache.values()) {
      const loan = entry.response?.data?.loans?.find((l) => this.stripEpsLoanPrefix(l.loanID) === normalizedId);
      if (loan) {
        for (const b of loan.borrowers) {
          allBorrowerIds.add(b.borrowerID);
        }
      }
    }
    return [...allBorrowerIds];
  }

  getDisplayBorrowerNameForLoan(loanId: string): string | null {
    const normalizedId = this.stripEpsLoanPrefix(loanId);
    for (const entry of this.dashboardCache.values()) {
      const loan = entry.response?.data?.loans?.find(
        (l) => this.stripEpsLoanPrefix(l.loanID) === normalizedId,
      );
      if (!loan) continue;
      const borrower =
        loan.borrowers.find((b) => b.isLoggedInBorrower) ??
        loan.borrowers.find((b) => b.position === 'Primary') ??
        loan.borrowers[0];
      const name = borrower ? this.formatBorrowerName(borrower.firstName, borrower.lastName) : '';
      return name || null;
    }
    return null;
  }

  private formatBorrowerName(firstName?: string | null, lastName?: string | null): string {
    return [firstName, lastName]
      .map((part) => part?.trim())
      .filter((part): part is string => !!part)
      .join(' ');
  }

  private stripEpsLoanPrefix(id: string): string {
    return id.startsWith('1_MTG_') ? id.substring(6) : id;
  }

  async getLoansByCategory(category: MlaLoanDashboardCategory): Promise<MlaLoanFacts[]> {
    const loans = await this.getAllLoans();
    return loans.filter((loan) => loan.loanDashboardCategory === category);
  }

  async getHistoricLoans(): Promise<MlaLoanFacts[]> {
    return this.getLoansByCategory('Historic');
  }

  async getPayingLoans(): Promise<MlaLoanFacts[]> {
    return this.getLoansByCategory('Paying');
  }

  async getRecentBankers(excludeInactive = true): Promise<string[]> {
    const url = `${this.mlaApiUrl}api/v1/dashboard/recent-bankers`;
    try {
      const params: Record<string, string> = excludeInactive ? { excludeInactive: 'true' } : {};
      const response = await firstValueFrom(this.http.get<{ success: boolean; data: string[] }>(url, { params }));
      if (response?.success && Array.isArray(response.data)) {
        return response.data;
      }
      this.loggingService.logWarning('[MlaDashboardService] recent-bankers response unsuccessful or no data');
      return [];
    } catch (error) {
      this.loggingService.logException(error, '[MlaDashboardService] Error fetching recent bankers');
      return [];
    }
  }

  private mapToLoanApplication(loan: MlaLoanFacts): MlaLoanApplicationModel {
    const borrower =
      loan.borrowers.find((b) => b.isLoggedInBorrower) ??
      loan.borrowers.find((b) => b.position === 'Primary') ??
      loan.borrowers[0];
    const address = this.formatAddress(loan.subjectAddress);
    const activeBorrowerIds = this.authService.getActiveBorrowerIds();
    const primaryBorrower = loan.borrowers.find((b) => b.position === 'Primary');

    return {
      id: loan.loanID,
      loanNumber: loan.displayLoanId,
      propertyAddress: address,
      status: loan.loanDashboardCategory,
      statusLabel: getBorrowerFriendlyStatus(loan.status),
      loanType: this.mapLoanType(loan.loanType),
      leadSource: loan.leadSource,
      tasksCount: borrower?.openTasks || 0,
      redirectURL: loan.redirectURL,
      lastModifiedDate: new Date(loan.lastModifiedDate),
      createdDate: new Date(loan.createdDate),
      newMonthlyPayment: undefined,
      newInterestRate: undefined,
      monthlySavings: undefined,
      estimatedClosingDate: undefined,
      servicingEnabledDate: loan.servicingEnabledDate,
      borrowerEmails: loan.borrowers.map((b) => b.email).filter(Boolean),
      borrowerNames: loan.borrowers.map((b) => this.formatBorrowerName(b.firstName, b.lastName)).filter(Boolean),
      currentUserIsPrimaryBorrower:
        !!primaryBorrower?.borrowerID && activeBorrowerIds.includes(primaryBorrower.borrowerID),
    };
  }

  private formatAddress(address: MlaAddress): string {
    if (!address) {
      return '';
    }
    const unit = address.unit ? ` ${address.unit}` : '';
    return `${address.street}${unit}, ${address.city}, ${address.state} ${address.zipCode}`;
  }

  private mapLoanType(loanType: string): 'Purchase' | 'Refinance' | 'HELOC' | 'HomeEquity' {
    const type = loanType ?? '';
    if (/purchase/i.test(type)) return 'Purchase';
    if (/heloc/i.test(type)) return 'HELOC';
    if (/home\s*equity/i.test(type)) return 'HomeEquity';
    return 'Refinance';
  }
}
